import React from 'react'
import styled from 'styled-components'
import cn from 'classnames'

import { LockIcon } from '../../lib/icons/lock-icon'
import { theme } from '../../lib/theme'

import Tooltip from './Tooltip'

export type ButtonGroupOption<T> = {
    label: string
    value: T
}

type GroupedButtonProps<T> = {
    id?: string
    value?: T | null
    options: ButtonGroupOption<T>[]
    onChange: (value: T) => void
    disabled?: boolean
    disabledReason?: string
}

const GroupedButtonContainer = styled.div`
    display: flex;
    border-radius: 14px;

    max-height: 45px;
    margin-top: 4px;
    height: 100%;
`

const GroupedButtonLock = styled.div`
    display: flex;
    border-radius: 0 14px 14px 0;
    background-color: ${({ theme }) => theme.font.backgroundColor};
    height: 100%;
`

const GroupedButtonChild = styled.button`
    background-color: unset;
    padding: 10px 15px;
    margin-right: 1px;
    background-color: ${({ theme }) => theme.font.backgroundColor};
    border: 1px solid ${({ theme }) => theme.input.borderColor};
    flex-grow: 1;
    justify-content: space-between;
    flex-basis: 0;
    transition: background-color 0.2s ease-in;
    min-width: 80px;

    &.active {
        background-color: ${({ theme }) => theme.brandColors.CA_Green};
    }

    &:first-child {
        border-top-left-radius: 13px;
        border-bottom-left-radius: 13px;
    }

    &:last-child {
        border-top-right-radius: 13px;
        border-bottom-right-radius: 13px;
    }
    &:disabled {
        cursor: not-allowed;
        &.active {
            background-color: ${({ theme }) => theme.brandColors.CA_Light_Green};
        }
    }
`

type Value = string

function GroupedButton<T = Value>({
    options,
    id = 'group-button',
    onChange,
    value,
    disabled,
    disabledReason,
}: GroupedButtonProps<T>) {
    return (
        <GroupedButtonContainer className="flex" id={id}>
            {options.map((option, index) => (
                <GroupedButtonChild
                    disabled={disabled}
                    key={`grouped-button-${index}`}
                    type="button"
                    className={cn({ active: value === option.value })}
                    onClick={() => {
                        onChange(option.value)
                    }}
                    data-testid={`${option.value ?? 'grouped'}-btn${value === option.value ? '-active' : ''}`}
                >
                    {option.label}
                </GroupedButtonChild>
            ))}
            {disabled && disabledReason && (
                <Tooltip content={disabledReason}>
                    <GroupedButtonLock>
                        <LockIcon
                            size={15}
                            fill={theme.brandColors.CA_Mid_Gray}
                            className="ml-4 mr-5"
                            data-testid="lock-icon"
                        />
                    </GroupedButtonLock>
                </Tooltip>
            )}
        </GroupedButtonContainer>
    )
}

export default GroupedButton
